import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { SVGS, Table } from "../components";
import { domain } from "../consts";
import { ModalContext } from "../context/ModalContext.jsx";

const InvitationDashboard = () => {
  const { rsvpId } = useParams();

  const { showModal } = useContext(ModalContext);
  const [responses, setResponses] = useState([]);
  const [loadingRes, setLoadingRes] = useState(true);
  const [numOfTables, setNumOfTables] = useState(0);
  const [originalNumOfTables, setOriginalNumOfTables] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [isEditingTable, setIsEditingTable] = useState(null);
  const [isEditingNumOfTables, setIsEditingNumOfTables] = useState(false);
  const [loadingAddTable, setLoadingAddTable] = useState(null);
  const [loadingNumOfTables, setLoadingNumOfTables] = useState(false);
  const [selectedTable, setSelectedTable] = useState(0);
  const [filteredData, setFilteredData] = useState([]);

  const [filterTable, setFilterTable] = useState(-1);
  const [filterAccommodation, setFilterAccommodation] = useState("");
  const [isComingLoading, setIsComingLoading] = useState(null);

  const itemsPerPage = 8;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const selectedData = filteredData?.slice(
    startIndex,
    startIndex + itemsPerPage,
  );
  const totalPages = Math.ceil(filteredData?.length / itemsPerPage);
  const totalGuests = responses?.reduce((acc, item) => {
    const mainGuestCount = item?.isComing ? 1 : 0;
    const additionalGuestsCount =
      item?.additionalGuests?.filter((guest) => guest.isComing)?.length || 0;
    return acc + mainGuestCount + additionalGuestsCount;
  }, 0);

  useEffect(() => {
    if (filterTable !== -1) {
      setFilteredData(
        responses?.reduce((acc, item) => {
          const additionalGuestsOnTable = item?.additionalGuests
            ?.filter((additional) => additional.table === Number(filterTable))
            .map((el) => ({
              ...el,
              additionalGuests: [],
              personalMessage: "",
              isAdditional: true,
              resId: item.id,
            }));

          if (
            additionalGuestsOnTable.length > 0 &&
            item?.table === Number(filterTable)
          ) {
            return [
              ...acc,
              { ...item, additionalGuests: [] },
              ...additionalGuestsOnTable,
            ];
          }

          if (additionalGuestsOnTable.length > 0) {
            return [...acc, ...additionalGuestsOnTable];
          }

          if (item?.table === Number(filterTable)) {
            return [...acc, { ...item, additionalGuests: [] }];
          }
          return acc;
        }, []),
      );
    } else {
      setFilteredData(
        responses?.filter((item) => {
          const matchesSearchQuery =
            item?.guestName
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase()) ||
            item?.additionalGuests?.some((additionalName) =>
              additionalName?.guestName
                ?.toLowerCase()
                .includes(searchQuery?.toLowerCase()),
            );

          const matchesAccommodationFilter = filterAccommodation
            ? item?.needsAccommodation === (filterAccommodation === "true") ||
              item?.additionalGuests?.some(
                (additional) =>
                  additional.needsAccommodation ===
                  (filterAccommodation === "true"),
              )
            : true;

          return matchesSearchQuery && matchesAccommodationFilter;
        }),
      );
    }
  }, [responses, searchQuery, filterTable, filterAccommodation]);

  useEffect(() => {
    getResponses();
  }, []);

  const getPageStyle = (page) =>
    page === currentPage
      ? "w-10 h-10 bg-teal-500 text-teal-500 text-white p-4 inline-flex items-center text-sm font-medium rounded-full cursor-pointer"
      : "w-10 h-10 text-gray-400 hover:text-teal-500 p-4 inline-flex items-center text-sm font-medium rounded-full cursor-pointer";

  const handleEditTable = async (id, isAdditional, resId = false) => {
    setLoadingAddTable(true);

    try {
      const response = await fetch(
        `${domain}/response/${rsvpId}/${
          isAdditional ? "additionalGuest" : "guest"
        }?guestId=${id}&table=${selectedTable}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      !isAdditional &&
        setResponses(
          responses.map((rs) => {
            if (rs.id !== id) return rs;
            return { ...rs, table: Number(selectedTable) };
          }),
        );

      isAdditional &&
        setResponses(
          responses.map((rs) => {
            if (resId && rs.id !== resId) return rs;
            const newAdditional = rs.additionalGuests.map((ag) => {
              if (ag.id !== id) return ag;
              return { ...ag, table: Number(selectedTable) };
            });
            return { ...rs, additionalGuests: newAdditional };
          }),
        );
      setLoadingAddTable(false);
      setIsEditingTable(false);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      setLoadingAddTable(false);
      setIsEditingTable(false);
    }
  };

  const handleEditNumOfTables = () => {
    if (numOfTables < originalNumOfTables) {
      showModal(
        {
          eventName: "Upozorenje",
          text: `Trenutni broj stolova je veci od odabranog, svi uzvanici koji su dodijeljeni za stolove ${
            Number(numOfTables) + 1
          } +`,
        },
        "update",
        updateNumOfTables,
      );
    } else {
      updateNumOfTables();
    }
  };

  const updateNumOfTables = async () => {
    setLoadingNumOfTables(true);
    try {
      const response = await fetch(
        `${domain}/tableNumber/${rsvpId}?numOfTables=${numOfTables}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      if (numOfTables < originalNumOfTables) {
        const newRes = responses.map((guest) => {
          if (guest.table > numOfTables) guest.table = 0;

          if (guest.additionalGuests.length) {
            const newAdditional = guest.additionalGuests.map((ag) => {
              if (ag.table > numOfTables) ag.table = 0;
              return ag;
            });

            guest.additionalGuests = newAdditional;
          }

          return guest;
        });

        setResponses(newRes);
      }

      setLoadingNumOfTables(false);
      setIsEditingNumOfTables(false);
      setOriginalNumOfTables(numOfTables);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      setLoadingNumOfTables(false);
      setIsEditingNumOfTables(false);
    }
  };

  const handleAttendanceChange = async (guestId, isMain, isComing) => {
    setIsComingLoading(guestId);
    try {
      const endpoint = isMain
        ? `${domain}/response/${rsvpId}/attendance/response/${guestId}`
        : `${domain}/response/${rsvpId}/attendance/additionalGuest/${guestId}`;

      const response = await fetch(endpoint, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isComing }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setResponses((prevResponses) =>
        prevResponses.map((res) => {
          if (isMain && res.id === guestId) {
            const table = isComing ? res.table : 0;
            return { ...res, isComing, table };
          } else if (!isMain) {
            const updatedAdditionalGuests = res.additionalGuests?.map(
              (guest) => {
                const table = isComing ? guest.table : 0;
                return guest.id === guestId
                  ? { ...guest, isComing, table }
                  : guest;
              },
            );
            return {
              ...res,
              additionalGuests: updatedAdditionalGuests,
            };
          }
          return res;
        }),
      );
    } catch (error) {
      console.error("Error updating attendance:", error);
    } finally {
      setIsComingLoading(null);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 4;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <a
          key={i}
          className={getPageStyle(i)}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </a>,
      );
    }

    return pageNumbers;
  };

  const handleExpandRow = (id) => {
    setExpandedRows(
      expandedRows.includes(id)
        ? expandedRows.filter((i) => i !== id)
        : [...expandedRows, id],
    );
  };

  const handleExpandMessage = (index) => {
    setExpandedRows(
      expandedRows.includes(index)
        ? expandedRows.filter((i) => i !== index)
        : [...expandedRows, index],
    );
  };

  const getResponses = async () => {
    try {
      const response = await fetch(`${domain}/response/${rsvpId}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      setResponses(data.responses);
      setNumOfTables(data.numOfTables);
      setOriginalNumOfTables(data.numOfTables);
      setLoadingRes(false);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      setLoadingRes(false);
    }
  };

  const headers = [
    { name: "Ime", property: "guestName" },
    { name: "Stol", property: "guestTable" },
    { name: "Treba smjestaj", property: "needsAccommodation" },
  ];

  return (
    <div className="max-w-[85rem] mb-20 px-4 py-4 sm:px-6 lg:px-8 lg:py-4 mx-auto grid grid-cols-10 gap-4 gap-y-14 h-screen">
      <div className="mb-12 sm:mb-4 py-4 col-span-10 md:col-span-2 w-full min-h-[20rem] max-h-[20rem]">
        <p className="mb-4 text-gray-600   text-xl font-bold">
          Filteri
        </p>
        <Filters
          filterTable={filterTable}
          setFilterTable={setFilterTable}
          numOfTables={numOfTables}
          originalNumOfTables={originalNumOfTables}
          filterAccommodation={filterAccommodation}
          setFilterAccommodation={setFilterAccommodation}
          isEditingNumOfTables={isEditingNumOfTables}
          loadingNumOfTables={loadingNumOfTables}
          setNumOfTables={setNumOfTables}
          setIsEditingNumOfTables={setIsEditingNumOfTables}
          handleEditNumOfTables={handleEditNumOfTables}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setCurrentPage={setCurrentPage}
        />
      </div>
      <div className="mb-4 py-4 col-span-10 md:col-span-8 w-full min-h-[20rem] max-h-[20rem]">
        <p className="mb-4 text-gray-600   text-xl font-bold">
          Svi uzvanici
        </p>
        <Table
          headers={headers}
          selectedData={selectedData}
          totalGuests={totalGuests}
          renderPageNumbers={renderPageNumbers}
          expandedRows={expandedRows}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          handleExpandRow={handleExpandRow}
          handleExpandMessage={handleExpandMessage}
          itemsPerPage={itemsPerPage}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedTable={selectedTable}
          setSelectedTable={setSelectedTable}
          isEditingTable={isEditingTable}
          setIsEditingTable={setIsEditingTable}
          handleEditTable={handleEditTable}
          loadingAddTable={loadingAddTable}
          numOfTables={numOfTables}
          handleEditNumOfTables={handleEditNumOfTables}
          isLoading={loadingRes}
          handleAttendanceChange={handleAttendanceChange}
          isComingLoading={isComingLoading}
        />
      </div>
    </div>
  );
};

export default InvitationDashboard;

const Filters = ({
  filterTable,
  setFilterTable,
  numOfTables,
  originalNumOfTables,
  filterAccommodation,
  setFilterAccommodation,
  isEditingNumOfTables,
  loadingNumOfTables,
  setNumOfTables,
  setIsEditingNumOfTables,
  handleEditNumOfTables,
  searchQuery,
  setSearchQuery,
  setCurrentPage,
}) => {
  return (
    <>
      <div className="mb-4 border rounded-2xl p-4">
        <div className="mb-2">
          <input
            type="text"
            placeholder="Pretrazi po imenu..."
            value={searchQuery}
            onChange={(e) => {
              setCurrentPage(1);
              setFilterTable(-1);
              setSearchQuery(e.target.value);
            }}
            className="p-2 border border-gray-300 rounded-xl w-full min-w-24"
          />
        </div>
        <div className="mb-2">
          <label className="block text-gray-700">Stol:</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-2xl"
            value={filterTable}
            onChange={(e) => {
              setFilterTable(Number(e.target.value));
              setFilterAccommodation("");
            }}
          >
            <option value={-1}>Svi stolovi</option>
            <option value={0} key={0}>
              Bez stola
            </option>
            {Array.from({ length: numOfTables }, (_, i) => i + 1).map(
              (table) => (
                <option key={table} value={table}>
                  Stol {table}
                </option>
              ),
            )}
          </select>
        </div>
        <div className="mb-2">
          <label className="block text-gray-700">Treba smjestaj:</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-2xl"
            value={filterAccommodation}
            onChange={(e) => {
              setFilterAccommodation(e.target.value);
              setFilterTable(-1);
            }}
          >
            <option value="">Svi</option>
            <option value="true">Da</option>
            <option value="false">Ne</option>
          </select>
        </div>
      </div>
      <p className="mb-4 text-gray-600   text-xl font-bold">
        Ukupan broj stolova
      </p>
      <div className="mb-4 border rounded-2xl p-4 w-full">
        <div className="mb-2 inline-flex flex-row items-center justify-between w-full">
          <input
            value={numOfTables}
            disabled={!isEditingNumOfTables}
            onChange={(e) => setNumOfTables(e.target.value)}
            className="w-3/4 p-2 border border-gray-300 rounded-2xl flex flex-shrink"
          />
          {!loadingNumOfTables && !isEditingNumOfTables && (
            <span
              onClick={() => setIsEditingNumOfTables(true)}
              className="ml-6 col-span-2 text-teal-600 cursor-pointer"
            >
              <SVGS.PencilSVG />
            </span>
          )}
          <div className="flex flex-row">
            <span
              onClick={() => {
                setIsEditingNumOfTables(false);
                setNumOfTables(originalNumOfTables);
              }}
              className={`ml-6 col-span-2 text-red-600 cursor-pointer ${
                (!isEditingNumOfTables || loadingNumOfTables) && "hidden"
              } `}
            >
              <SVGS.PencilOffSVG />
            </span>
            <span
              onClick={handleEditNumOfTables}
              className={`hs-dropup-toggle ml-6 col-span-2 text-teal-600 cursor-pointer ${
                !isEditingNumOfTables ||
                numOfTables >= originalNumOfTables ||
                loadingNumOfTables
                  ? "hidden"
                  : ""
              }`}
              data-hs-overlay="#hs-slide-up-animation-modal"
            >
              <SVGS.CheckmarkSVG />
            </span>
            <span
              onClick={handleEditNumOfTables}
              className={`ml-6 col-span-2 text-teal-600 cursor-pointer ${
                !isEditingNumOfTables ||
                numOfTables < originalNumOfTables ||
                loadingNumOfTables
                  ? "hidden"
                  : ""
              }`}
            >
              <SVGS.CheckmarkSVG />
            </span>
          </div>
          {loadingNumOfTables && (
            <span
              className="ml-6 col-span-2 rounded-full animate-spin inline-block w-6 h-6 border-4 border-current border-t-transparent text-teal-600 "
              role="status"
              aria-label="loading"
            ></span>
          )}
        </div>
      </div>
    </>
  );
};
